/**
 * Task #11: Autosave draft in localStorage
 * Utility pentru salvarea si restaurarea draft-urilor de formulare
 */

const DRAFT_PREFIX = 'form_draft_';
const DRAFT_EXPIRY_DAYS = 7;

export interface FormDraft<T> {
  data: T;
  timestamp: number;
  userId?: string;
}

/**
 * Salveaza un draft de formular in localStorage
 */
export const saveDraft = <T>(
  formId: string,
  data: T,
  userId?: string
): void => {
  try {
    const draft: FormDraft<T> = {
      data,
      timestamp: Date.now(),
      userId,
    };

    const key = `${DRAFT_PREFIX}${formId}`;
    localStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    console.error('Error saving draft:', error);
  }
};

/**
 * Incarca un draft de formular din localStorage
 */
export const loadDraft = <T>(
  formId: string,
  userId?: string
): T | null => {
  try {
    const key = `${DRAFT_PREFIX}${formId}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const draft: FormDraft<T> = JSON.parse(stored);

    // Verifica daca draft-ul a expirat
    const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const isExpired = Date.now() - draft.timestamp > expiryTime;

    if (isExpired) {
      deleteDraft(formId);
      return null;
    }

    // Verifica daca draft-ul apartine utilizatorului curent
    if (userId && draft.userId && draft.userId !== userId) {
      return null;
    }

    return draft.data;
  } catch (error) {
    console.error('Error loading draft:', error);
    return null;
  }
};

/**
 * Sterge un draft de formular din localStorage
 */
export const deleteDraft = (formId: string): void => {
  try {
    const key = `${DRAFT_PREFIX}${formId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error deleting draft:', error);
  }
};

/**
 * Verifica daca exista un draft pentru formular
 */
export const hasDraft = (formId: string, userId?: string): boolean => {
  const draft = loadDraft(formId, userId);
  return draft !== null;
};

/**
 * Obtine data la care a fost salvat draft-ul
 */
export const getDraftTimestamp = (formId: string): Date | null => {
  try {
    const key = `${DRAFT_PREFIX}${formId}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const draft: FormDraft<any> = JSON.parse(stored);
    return new Date(draft.timestamp);
  } catch (error) {
    console.error('Error getting draft timestamp:', error);
    return null;
  }
};

/**
 * Curata toate draft-urile expirate
 */
export const cleanExpiredDrafts = (): void => {
  try {
    const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const keys = Object.keys(localStorage);

    keys.forEach((key) => {
      if (key.startsWith(DRAFT_PREFIX)) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const draft: FormDraft<any> = JSON.parse(stored);
            const isExpired = Date.now() - draft.timestamp > expiryTime;
            
            if (isExpired) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          // Sterge si cheile invalide
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Error cleaning expired drafts:', error);
  }
};

